import { useCallback, useEffect, useState } from 'react';
import { getUser } from '@frontend/auth-helpers';

const LOCATION_PICKER_DEMO_UI_STORAGE_KEY_PREFIX = 'location-picker:show-demo-ui';
const LOCATION_PICKER_DEMO_UI_CHANGE_EVENT = 'location-picker:show-demo-ui-change';

const getLocationPickerDemoUiStorageKey = () => {
  return `${LOCATION_PICKER_DEMO_UI_STORAGE_KEY_PREFIX}:${getUser()?.userID ?? 'anonymous'}`;
};

const readLocationPickerDemoUiEnabled = () => {
  if (typeof window === 'undefined') {
    return false;
  }

  return window.localStorage.getItem(getLocationPickerDemoUiStorageKey()) === 'true';
};

export const setLocationPickerDemoUiEnabled = (enabled: boolean) => {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.setItem(getLocationPickerDemoUiStorageKey(), String(enabled));
  window.dispatchEvent(
    new CustomEvent<boolean>(LOCATION_PICKER_DEMO_UI_CHANGE_EVENT, {
      detail: enabled,
    })
  );
};

export const useLocationPickerDemoUiEnabled = () => {
  const [isEnabled, setIsEnabled] = useState(readLocationPickerDemoUiEnabled);

  useEffect(() => {
    const syncValue = () => {
      setIsEnabled(readLocationPickerDemoUiEnabled());
    };

    window.addEventListener('storage', syncValue);
    window.addEventListener(LOCATION_PICKER_DEMO_UI_CHANGE_EVENT, syncValue);

    return () => {
      window.removeEventListener('storage', syncValue);
      window.removeEventListener(LOCATION_PICKER_DEMO_UI_CHANGE_EVENT, syncValue);
    };
  }, []);

  return isEnabled;
};

export const useLocationPickerDemoUiControl = () => {
  const isEnabled = useLocationPickerDemoUiEnabled();

  const setIsEnabled = useCallback((enabled: boolean) => {
    setLocationPickerDemoUiEnabled(enabled);
  }, []);

  return {
    isEnabled,
    setIsEnabled,
  };
};
